import { useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { api, PAPELES, type FichaContenedor as Ficha, type PalletEliminado, type TipoPallet } from '../api';
import type { Vista } from '../components/Layout';
import { abrir, CURVA, fila as entrada, suave } from '../components/movimiento';
import { Dato, EdicionContenedor, preguntaDeBorrado } from '../components/EdicionContenedor';
import {
  Buscador,
  Cargando,
  ChipBoton,
  EstadoChip,
  filtrar,
  Icono,
  Kpi,
  Numero,
  Paginador,
  PanelError,
  useConfirmar,
  usePaginado,
  Vacio,
} from '../components/ui';
import { pestanasDeFicha, type Pestana } from '../lib/fichaContenedor';
import { useEstadoGuardado } from '../lib/persistencia';
import { useEsAdmin, useSesion } from '../lib/sesion';
import { mensajeDeError, useApi, VIVO } from '../lib/useApi';
import { preguntaRestablecer, TarjetaEliminado, textoRestablecido } from './PalletsEliminados';
import {
  aviso,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  etiquetaCampo,
  encabezadoTabla,
  enlace,
  miles,
  mono,
  tarjeta,
} from '../theme';

/**
 * La ficha de un contenedor: todo lo que bajó de él en una pantalla. Sus
 * pallets y dónde están, lo que declaraba el borrador, lo que ya salió, los
 * datos de la factura para corregir y lo que se eliminó.
 */
export default function FichaContenedor({
  id,
  onIr,
}: {
  id: string | null;
  onIr: (v: Vista, id?: string) => void;
}) {
  const esAdmin = useEsAdmin();
  const ficha = useApi(() => (id ? api.fichaContenedor(id) : Promise.resolve(null)), [id], VIVO.normal);
  // Los muelles sólo hacen falta para editar, que es de administración.
  const muelles = useApi(
    () => (esAdmin ? api.contenedorNuevo().then((d) => d.muelles) : Promise.resolve([] as string[])),
    [esAdmin],
  );
  const [pestana, setPestana] = useEstadoGuardado<Pestana>('contenedor.pestana', 'resumen', (v) => typeof v === 'string');
  const [editando, setEditando] = useState(false);
  // Hacia dónde se movió la pestaña: el contenido nuevo entra desde ese lado.
  const [sentido, setSentido] = useState(1);
  const [hecho, setHecho] = useState('');
  const [fallo, setFallo] = useState('');
  const [confirmar, dialogo] = useConfirmar();

  if (!id)
    return (
      <div style={tarjeta}>
        <Vacio icono="directions_boat" titulo="Elegí un contenedor" detalle="Se abre tocando su código en la lista de contenedores." />
      </div>
    );
  if (ficha.error) return <PanelError mensaje={ficha.error} onReintentar={ficha.recargar} />;
  if (!ficha.datos) return <Cargando texto="Buscando el contenedor…" />;

  const f = ficha.datos;
  const ct = f.datos;
  const pestanas = pestanasDeFicha(f);
  const activa = pestanas.some((p) => p.id === pestana) ? pestana : 'resumen';

  async function borrar() {
    if (!(await confirmar(preguntaDeBorrado(ct)))) return;
    setFallo('');
    try {
      await api.borrarContenedor(ct.id);
      onIr('papeles');
    } catch (err) {
      setFallo(mensajeDeError(err));
    }
  }

  async function ponerEnDescarga() {
    setFallo('');
    setHecho('');
    try {
      await api.ponerEnDescarga(ct.id);
      setHecho(`${ct.id} quedó en descarga.`);
      ficha.recargar();
    } catch (err) {
      setFallo(mensajeDeError(err));
    }
  }

  // El pallet se eliminó desde el escáner y sus piezas quedaron esperando
  // pallet: si esa mercadería no existía, el contenedor no termina nunca.
  async function quitarDeclarado(e: PalletEliminado) {
    const ok = await confirmar({
      titulo: `¿Quitar ${e.pallet} de lo declarado?`,
      detalle: `Sus piezas dejan de esperar pallet en Armado y el contenedor declara un pallet menos. Hacelo si esa mercadería no existe (un pallet cargado de más); si existe, armale un pallet nuevo.`,
      boton: 'Quitar de lo declarado',
    });
    if (!ok) return;
    setFallo('');
    setHecho('');
    try {
      const r = await api.quitarDeclaradoDeEliminado(e.id);
      setHecho(r.piezas > 0 ? `${e.pallet} salió de lo declarado (${miles(r.piezas)} piezas).` : `${e.pallet} no tenía piezas pendientes.`);
      ficha.recargar();
    } catch (err) {
      setFallo(mensajeDeError(err));
    }
  }

  async function restablecer(e: PalletEliminado) {
    if (!(await confirmar(preguntaRestablecer(e)))) return;
    setFallo('');
    setHecho('');
    try {
      setHecho(textoRestablecido(await api.restablecerEliminado(e.id)));
      ficha.recargar();
    } catch (err) {
      setFallo(mensajeDeError(err));
    }
  }

  const ubicados = ct.procesados;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 1200 }}>
      {/* ------------------------------------------------------- cabecera */}
      <div style={{ ...tarjeta, padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontFamily: mono, fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', color: c.texto }}>
            {ct.id}
          </h1>
          {ct.numero != null && <span style={{ fontSize: 15, fontWeight: 600, color: c.texto2 }}>N° {ct.numero}</span>}
          <EstadoChip estado={ct.estado} />
        </div>
        <div style={{ fontSize: 13.5, color: c.texto2 }}>
          Arribo {ct.fecha} · {ct.muelle ? `muelle ${ct.muelle}` : 'sin muelle'} · {ct.pallets}{' '}
          {ct.pallets === 1 ? 'pallet declarado' : 'pallets declarados'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={ct.pallets}
            aria-valuenow={ubicados}
            style={{ flex: '1 1 200px', maxWidth: 360, height: 8, borderRadius: 999, background: c.fondo, overflow: 'hidden' }}
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${ct.pallets ? Math.min(100, (ubicados / ct.pallets) * 100) : 0}%` }}
              transition={{ duration: 0.7, ease: CURVA }}
              style={{ height: '100%', background: c.verde }}
            />
          </div>
          <span style={{ fontSize: 13, color: c.texto2, fontVariantNumeric: 'tabular-nums' }}>
            <Numero valor={ubicados} /> de <Numero valor={ct.pallets} /> ubicados
          </span>
        </div>
        {/* Celular: la acción principal a lo ancho y Editar/Borrar repartidos debajo; desde tablet, la fila de siempre. */}
        <div className="grid grid-cols-1 gap-2.5 tablet:flex tablet:flex-wrap">
          {ct.estado === 'En tránsito' && (
            <button type="button" onClick={() => void ponerEnDescarga()} style={botonPrimario}>
              <Icono nombre="unarchive" size={18} />
              Poner en descarga
            </button>
          )}
          <button type="button" onClick={() => onIr('etiquetasPallet', ct.id)} style={botonSecundario}>
            <Icono nombre="print" size={18} />
            Imprimir etiquetas de pallet
          </button>
          {esAdmin && (
            <>
              <button
                type="button"
                onClick={() => {
                  setHecho('');
                  setPestana('datos');
                  setEditando(true);
                }}
                style={botonSecundario}
              >
                <Icono nombre="edit" size={18} />
                Editar datos
              </button>
              <button type="button" onClick={() => void borrar()} style={{ ...botonSecundario, color: c.rojo }}>
                <Icono nombre="delete" size={18} color={c.rojo} />
                Borrar contenedor
              </button>
            </>
          )}
        </div>
      </div>

      <AnimatePresence>
        {hecho && (
          <motion.div key={hecho} role="status" {...abrir(16)} style={aviso('ok')}>
            {hecho}
          </motion.div>
        )}
        {fallo && (
          <motion.div key={fallo} role="alert" {...abrir(16)} style={aviso('err')}>
            {fallo}
          </motion.div>
        )}
      </AnimatePresence>

      {/* -------------------------------------------------------- pestañas */}
      <div className="wms-tira">
        {pestanas.map((p, n) => (
          <ChipBoton
            key={p.id}
            grupo="ficha-pestana"
            activo={activa === p.id}
            onClick={() => {
              setSentido(n >= pestanas.findIndex((x) => x.id === activa) ? 1 : -1);
              setPestana(p.id);
            }}
          >
            {p.texto}
          </ChipBoton>
        ))}
      </div>

      {/* El contenido entra de nuevo al cambiar de pestaña. */}
      <motion.div key={activa} initial={{ opacity: 0, x: sentido * 24 }} animate={{ opacity: 1, x: 0 }} transition={suave}>
      {activa === 'resumen' && <Resumen f={f} onIr={onIr} />}
      {activa === 'pallets' && <Pallets f={f} onIr={onIr} onCambio={ficha.recargar} />}
      {activa === 'recepcion' && <Recepcion f={f} onIr={onIr} />}
      {activa === 'salidas' && <Salidas f={f} onIr={onIr} />}
      {activa === 'datos' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* La tarjeta es una sola: al pasar de ver a editar cambia de alto con `layout` y el contenido nace adentro. */}
          <motion.div layout transition={suave} style={{ ...tarjeta, overflow: 'hidden', position: 'relative' }}>
            {/* `popLayout`: lo que sale deja de ocupar lugar al instante y la tarjeta se estira hasta el alto nuevo mientras entra el otro. */}
            <AnimatePresence initial={false} mode="popLayout">
            {editando ? (
              <EdicionContenedor
                key="edicion"
                ct={ct}
                muelles={muelles.datos ?? []}
                onCancelar={() => setEditando(false)}
                onGuardado={(id) => {
                  setEditando(false);
                  setHecho(`Se guardaron los datos de ${id}.`);
                  // Con el código corregido, la ficha vieja ya no existe.
                  if (id !== ct.id) onIr('contenedor', id);
                  else ficha.recargar();
                }}
              />
            ) : (
              <motion.div key="vista" {...abrir(0)} transition={{ ...abrir(0).transition, delay: 0.12 }} style={{ overflow: 'hidden', padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div
                  style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13.5, maxWidth: 560 }}
                >
                  {PAPELES.map((p) => (
                    <Fila key={p.campo} rotulo={p.nombre}>
                      <Dato valor={ct[p.campo]} />
                    </Fila>
                  ))}
                  <Fila rotulo="Nº de contenedor">{ct.numero ?? '—'}</Fila>
                  <Fila rotulo="Fecha de arribo">{ct.fecha}</Fila>
                  <Fila rotulo="Muelle">{ct.muelle ?? 'Sin asignar'}</Fila>
                  <Fila rotulo="Recepción">
                    {f.recepcion.confirmado
                      ? `Confirmada${f.recepcion.confirmadoEn ? ` el ${f.recepcion.confirmadoEn}` : ''}`
                      : f.recepcion.borradorEn
                        ? `Borrador del ${f.recepcion.borradorEn}, sin confirmar`
                        : 'Sin borrador'}
                  </Fila>
                </div>
                {esAdmin && (
                  <button
                    type="button"
                    onClick={() => {
                      setHecho('');
                      setEditando(true);
                    }}
                    style={{ ...botonSecundario, alignSelf: 'flex-start' }}
                  >
                    Editar datos
                  </button>
                )}
              </motion.div>
            )}
            </AnimatePresence>
          </motion.div>

          <motion.div layout="position" transition={suave} style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Documentos adjuntos</span>
            {f.documentos.length === 0 ? (
              <span style={{ fontSize: 13, color: c.texto3 }}>No tiene documentos adjuntos.</span>
            ) : (
              f.documentos.map((d) => (
                <span key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
                  <Icono nombre="description" size={18} color={c.texto3} />
                  {d.nombre}
                  <span style={{ color: c.texto3 }}>{d.peso}</span>
                </span>
              ))
            )}
          </motion.div>
        </div>
      )}
      {activa === 'eliminados' && (
        <div style={{ ...tarjeta, overflow: 'hidden' }}>
          {f.eliminados.map((e) => (
            <TarjetaEliminado
              key={e.id}
              e={e}
              onQuitarDeclarado={esAdmin ? () => void quitarDeclarado(e) : undefined}
              onRestablecer={esAdmin ? () => void restablecer(e) : undefined}
            />
          ))}
        </div>
      )}
      </motion.div>
      {dialogo}
    </div>
  );
}

function Fila({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <>
      <span style={{ color: c.texto3 }}>{rotulo}</span>
      <span style={{ color: c.texto, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </>
  );
}

type Columna<T> = { titulo: string; ancho?: number; derecha?: boolean; celda: (fila: T) => ReactNode };

/**
 * Una tabla de la ficha: la tabla de siempre en tablet y escritorio, y una
 * tarjeta por fila en el celular (`.wms-tarjetas`). La primera columna es el
 * título de la tarjeta; la que no tiene título (el botón de eliminar) va abajo.
 * `resaltar` pinta el fondo de los que piden atención.
 */
function Tabla<T>({
  columnas,
  filas,
  clave,
  resaltar,
}: {
  columnas: Columna<T>[];
  filas: T[];
  clave: (fila: T) => string | number;
  resaltar?: (fila: T) => string | undefined;
}) {
  const ancho = (col: Columna<T>) => (col.ancho ? { width: col.ancho } : { minWidth: 160 });
  return (
    <div className="wms-scroll-x">
      <table className="wms-tarjetas" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, color: c.texto }}>
        <thead>
          <tr>
            {columnas.map((col, n) => (
              <th
                key={col.titulo || n}
                className="px-2 py-2.5 first:pl-4 last:pr-4"
                style={{ ...encabezadoTabla, display: 'table-cell', padding: undefined, ...ancho(col), textAlign: col.derecha ? 'right' : 'left' }}
              >
                {col.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila, i) => (
            <motion.tr
              key={clave(fila)}
              {...entrada(i)}
              style={{ background: resaltar?.(fila) }}
            >
              {columnas.map((col, n) => (
                <td
                  key={col.titulo || n}
                  className={`px-2 py-3 align-top first:pl-4 last:pr-4${n === 0 ? ' wms-principal' : col.titulo ? '' : ' wms-acciones'}`}
                  data-rotulo={n > 0 && col.titulo ? col.titulo : undefined}
                  style={{ borderBottom: `1px solid ${c.borde}`, textAlign: col.derecha ? 'right' : undefined, minWidth: 0 }}
                >
                  {col.celda(fila)}
                </td>
              ))}
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const verAviso = (n: HTMLDivElement | null) => n?.scrollIntoView({ block: 'center', behavior: 'smooth' });

const numero = (n: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{miles(n)}</span>;

function Resumen({ f, onIr }: { f: Ficha; onIr: (v: Vista, id?: string) => void }) {
  const r = f.resumen;
  const puedeUbicar = useSesion().vistas.includes('ubicar');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Dos columnas en el celular (los números son cortos); desde tablet, la grilla de siempre. */}
      <div className="grid grid-cols-2 gap-3 tablet:grid-cols-[repeat(auto-fit,minmax(190px,1fr))] tablet:gap-3.5">
        <motion.div className="grid" {...entrada(0)}>
          <Kpi
            rotulo="En el sistema"
            valor={r.pallets}
            pie={`${r.fraccionamiento} de fraccionamiento · ${r.completos} completos`}
          />
        </motion.div>
        <motion.div className="grid" {...entrada(1)}>
          <Kpi rotulo="En rack" valor={r.enRack} pie={`${r.enZonaDePiso} en zona de piso`} />
        </motion.div>
        <motion.div className="flex flex-col [&>:first-child]:flex-1" {...entrada(2)}>
          <Kpi
            rotulo="Sin ubicar"
            valor={r.sinUbicar}
            pie={r.sinUbicar ? 'Falta asignarles posición' : 'Todo ubicado'}
            color={r.sinUbicar ? c.ambar : undefined}
          />
          {r.sinUbicar > 0 && puedeUbicar && (
            <button type="button" onClick={() => onIr('ubicar')} style={{ ...enlace, fontSize: 13, marginTop: 8 }}>
              Ubicarlos ahora
            </button>
          )}
        </motion.div>
        <motion.div className="grid" {...entrada(3)}>
          <Kpi rotulo="Despachados" valor={r.despachados} pie="Salieron enteros del depósito" />
        </motion.div>
        <motion.div className="grid col-span-2 tablet:col-auto" {...entrada(4)}>
          <Kpi
            rotulo="Piezas arriba"
            valor={r.piezasArriba}
            pie={`de ${miles(r.piezasIniciales)} · ${miles(r.piezasEntregadas)} entregadas`}
          />
        </motion.div>
      </div>

      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${c.borde}`, fontSize: 15, fontWeight: 600 }}>
          Por artículo
        </div>
        {f.porArticulo.length === 0 ? (
          <Vacio icono="inventory_2" titulo="Sin pallets" detalle="Todavía no bajó ningún pallet de este contenedor." />
        ) : (
          <Tabla
            filas={f.porArticulo}
            clave={(a) => a.articulo}
            columnas={[
              { titulo: 'Artículo', celda: (a) => a.etiqueta },
              { titulo: 'Pallets', ancho: 80, derecha: true, celda: (a) => numero(a.pallets) },
              { titulo: 'Iniciales', ancho: 100, derecha: true, celda: (a) => numero(a.piezas) },
              { titulo: 'Arriba', ancho: 100, derecha: true, celda: (a) => numero(a.arriba) },
              { titulo: 'Entregadas', ancho: 110, derecha: true, celda: (a) => numero(a.entregadas) },
            ]}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Un pallet que no estaba, armado de cero: cualquier artículo (si no está en
 * el maestro se da de alta), su lote y sus piezas. Se suma a lo declarado del
 * contenedor y queda sin ubicar.
 */
function NuevoPallet({ contenedor, onListo }: { contenedor: string; onListo: (mensaje: string) => void }) {
  const maestro = useApi(() => api.contenedorNuevo().then((d) => d.articulos), []);
  const vacio = { articulo: '', descripcion: '', lote: '', numeroParte: '', piezas: '', tipo: 'fraccionamiento' as TipoPallet };
  const [form, setForm] = useState(vacio);
  const [guardando, setGuardando] = useState(false);
  const [problema, setProblema] = useState('');
  const conocido = maestro.datos?.find((a) => a.codigo.toUpperCase() === form.articulo.trim().toUpperCase());
  const listo = form.articulo.trim() !== '' && Number(form.piezas) > 0;
  const poner = (k: keyof typeof vacio) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  async function crear() {
    setProblema('');
    setGuardando(true);
    try {
      const r = await api.crearPalletEnContenedor(contenedor, { ...form, piezas: Number(form.piezas) });
      setForm(vacio);
      onListo(`Se armó el pallet ${r.pallet}. Está sin ubicar.`);
    } catch (err) {
      setProblema(mensajeDeError(err));
    } finally {
      setGuardando(false);
    }
  }

  const caja = (flex: string) => ({ display: 'flex', flexDirection: 'column' as const, gap: 6, flex });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (listo) void crear();
      }}
      style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}
    >
      <label style={caja('1 1 180px')}>
        <span style={etiquetaCampo}>Artículo</span>
        <input list="nuevo-pallet-articulos" value={form.articulo} onChange={poner('articulo')} style={campo} />
        <datalist id="nuevo-pallet-articulos">
          {maestro.datos?.map((a) => (
            <option key={a.codigo} value={a.codigo}>
              {a.etiqueta}
            </option>
          ))}
        </datalist>
      </label>
      <label style={caja('2 1 220px')}>
        <span style={etiquetaCampo}>Descripción{conocido ? '' : ' (artículo nuevo)'}</span>
        <input
          value={conocido ? conocido.descripcion : form.descripcion}
          onChange={poner('descripcion')}
          disabled={Boolean(conocido)}
          style={{ ...campo, opacity: conocido ? 0.6 : 1 }}
        />
      </label>
      <label style={caja('1 1 120px')}>
        <span style={etiquetaCampo}>Lote</span>
        <input value={form.lote} onChange={poner('lote')} style={campo} />
      </label>
      <label style={caja('1 1 120px')}>
        <span style={etiquetaCampo}>N° de parte</span>
        <input value={form.numeroParte} onChange={poner('numeroParte')} style={campo} />
      </label>
      <label style={caja('1 1 100px')}>
        <span style={etiquetaCampo}>Piezas</span>
        <input type="number" inputMode="numeric" min={1} value={form.piezas} onChange={poner('piezas')} style={campo} />
      </label>
      <label style={caja('2 1 150px')}>
        <span style={etiquetaCampo}>Clase</span>
        <select value={form.tipo} onChange={poner('tipo')} style={campo}>
          <option value="fraccionamiento">Fraccionamiento</option>
          <option value="completo">Completo</option>
        </select>
      </label>
      <button
        type="submit"
        disabled={guardando || !listo}
        className="w-full tablet:w-auto"
        style={{ ...botonPrimario, minHeight: 44, opacity: guardando || !listo ? 0.5 : 1 }}
      >
        <Icono nombre="add" size={18} />
        Crear pallet
      </button>
      <AnimatePresence>
        {problema && (
          <motion.div key={problema} role="alert" {...abrir(16)} style={{ ...aviso('err'), flexBasis: '100%' }}>
            {problema}
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
}

function Pallets({ f, onIr, onCambio }: { f: Ficha; onIr: (v: Vista, id?: string) => void; onCambio: () => void }) {
  const esAdmin = useEsAdmin();
  const puedeVerMapa = useSesion().vistas.includes('racks');
  const [confirmar, dialogo] = useConfirmar();
  const [hecho, setHecho] = useState('');
  const [fallo, setFallo] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const encontrados = filtrar(f.pallets, busqueda, (p) => [
    p.id,
    p.estado,
    p.ubicacion,
    ...p.items.flatMap((i) => [i.articulo, i.descripcion, i.numeroParte, i.lote]),
  ]);
  const pagina = usePaginado(encontrados);

  // Sólo administración, y con motivo: queda en «Pallets eliminados».
  async function eliminar(p: Ficha['pallets'][number]) {
    setHecho('');
    setFallo('');
    const salidas = f.salidas.filter((x) => x.pallet === p.id).length;
    const motivo = await confirmar({
      titulo: `¿Eliminar ${p.id} del sistema?`,
      detalle: `${p.articulo} · ${p.tipo === 'completo' ? 'completo' : `${miles(p.arriba)} pz`} · ${p.ubicacion ?? 'sin ubicar'}. Sale del contenedor por completo, con sus artículos: no queda nada pendiente de paletizar.${
        salidas ? ` Tiene ${salidas} ${salidas === 1 ? 'salida' : 'salidas'}: lo que una orden pidió y no salió vuelve a «Pedido» para tomarlo de otro pallet; lo entregado se borra.` : ''
      } No se puede deshacer; queda registrado en Pallets eliminados.`,
      boton: 'Eliminar pallet',
      motivo: 'Motivo',
    });
    if (motivo === null) return;
    try {
      await api.eliminarPallet(p.id, motivo, true);
      setHecho(`${p.id} se eliminó del sistema.`);
      onCambio();
    } catch (err) {
      setFallo(mensajeDeError(err));
    }
  }

  const columnaEliminar = {
    titulo: '',
    ancho: 48,
    celda: (p: Ficha['pallets'][number]) => (
      <button
        type="button"
        onClick={() => void eliminar(p)}
        aria-label={`Eliminar ${p.id}`}
        title="Eliminar del sistema"
        style={{ width: 36, height: 36, borderRadius: 999, border: 'none', background: 'none', color: c.rojo, cursor: 'pointer' }}
      >
        <Icono nombre="delete" size={18} />
      </button>
    ),
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: c.texto }}>Nuevo pallet</span>
        <NuevoPallet
          contenedor={f.datos.id}
          onListo={(m) => {
            setFallo('');
            setHecho(m);
            onCambio();
          }}
        />
      </div>
      {/* La tabla puede ser larga: el aviso se trae a la vista, si no parece que no pasó nada. */}
      <AnimatePresence>
        {hecho && (
          <motion.div key={hecho} role="status" ref={verAviso} {...abrir(16)} style={aviso('ok')}>
            {hecho}
          </motion.div>
        )}
        {fallo && (
          <motion.div key={fallo} role="alert" ref={verAviso} {...abrir(16)} style={aviso('err')}>
            {fallo}
          </motion.div>
        )}
      </AnimatePresence>
      <Buscador valor={busqueda} onCambiar={setBusqueda} placeholder="Pallet, artículo, lote o ubicación" />
      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        {encontrados.length === 0 ? (
          <Vacio icono="inventory_2" titulo="Sin pallets" detalle={busqueda ? `Ninguno coincide con «${busqueda}».` : 'Todavía no bajó ninguno.'} />
        ) : (
          <Tabla
            filas={pagina.visibles}
            clave={(p) => p.id}
            columnas={[
              {
                titulo: 'Pallet',
                ancho: 130,
                celda: (p) => (
                  <button type="button" onClick={() => onIr('frac', p.id)} title="Ver la ficha del pallet" style={{ ...enlace, fontSize: 13 }}>
                    {p.id}
                  </button>
                ),
              },
              { titulo: 'Artículo', celda: (p) => <Items p={p} /> },
              {
                titulo: 'Piezas',
                ancho: 110,
                derecha: true,
                celda: (p) =>
                  p.tipo === 'completo' ? (
                    <span style={{ color: c.texto3 }}>entero</span>
                  ) : (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {miles(p.arriba)}
                      {p.arriba !== p.piezas && <span style={{ color: c.texto3 }}> de {miles(p.piezas)}</span>}
                    </span>
                  ),
              },
              {
                titulo: 'Ubicación',
                ancho: 150,
                celda: (p) =>
                  p.ubicacion ? (
                    puedeVerMapa ? (
                      <button type="button" onClick={() => onIr('racks', p.ubicacion!)} title="Ver la posición en el mapa" style={{ ...enlace, fontSize: 12.5 }}>
                        {p.ubicacion}
                      </button>
                    ) : (
                      <span style={{ fontFamily: mono, fontSize: 12.5 }}>{p.ubicacion}</span>
                    )
                  ) : (
                    <span style={{ color: p.estado === 'Despachado' ? c.texto3 : c.ambar, fontWeight: 600, fontSize: 12.5 }}>
                      {p.estado === 'Despachado' ? 'Salió' : 'Sin ubicar'}
                    </span>
                  ),
              },
              { titulo: 'Estado', ancho: 170, celda: (p) => <EstadoChip estado={p.estado} /> },
              { titulo: 'Ingreso', ancho: 100, celda: (p) => p.fechaIngreso },
              ...(esAdmin ? [columnaEliminar] : []),
            ]}
          />
        )}
        <Paginador
          pagina={pagina.pagina}
          paginas={pagina.paginas}
          total={pagina.total}
          desde={pagina.desde}
          que="pallets"
          onIr={pagina.setPagina}
        />
      </div>
      {dialogo}
    </div>
  );
}

/** El artículo principal y, si hay más, «+N» que despliega todas las líneas. */
function Items({ p }: { p: Ficha['pallets'][number] }) {
  return (
    <details style={{ fontSize: 13 }}>
      <summary style={{ cursor: 'pointer' }}>
        {p.articulo}
        {p.articulos > 1 && <span style={{ color: c.texto3 }}> +{p.articulos - 1}</span>}
        {p.tipo === 'completo' && <span style={{ color: c.texto3 }}> · completo</span>}
      </summary>
      {p.items.map((i, n) => (
        <div key={n} style={{ marginTop: 4, fontSize: 12, color: c.texto2, textWrap: 'pretty' }}>
          <span style={{ fontFamily: mono, color: c.texto }}>{i.articulo}</span>
          {[i.descripcion, i.numeroParte && `N° parte ${i.numeroParte}`, i.lote && `lote ${i.lote}`, `${miles(i.piezas)} pz`]
            .filter(Boolean)
            .map((x) => ` · ${x}`)
            .join('')}
        </div>
      ))}
    </details>
  );
}

function Recepcion({ f, onIr }: { f: Ficha; onIr: (v: Vista, id?: string) => void }) {
  const r = f.recepcion;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ fontSize: 13.5, color: c.texto2 }}>
        {r.confirmado ? `Confirmada${r.confirmadoEn ? ` el ${r.confirmadoEn}` : ''}` : `Borrador del ${r.borradorEn ?? '—'}, sin confirmar`}
        {' · '}
        <Numero valor={r.leidos} /> de <Numero valor={r.pallets} /> leídos
        {r.sinLeer > 0 && <span style={{ color: c.ambar, fontWeight: 600 }}> · <Numero valor={r.sinLeer} /> sin leer</span>}
        {r.diferencias > 0 && (
          <span style={{ color: c.rojo, fontWeight: 600 }}>
            {' '}
            · <Numero valor={r.diferencias} desdeCero={false} /> con {r.diferencias === 1 ? 'diferencia' : 'diferencias'}
          </span>
        )}
      </div>
      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        <Tabla
          filas={r.lineas}
          clave={(l) => l.id}
          resaltar={(l) => (!l.leido ? c.ambarSuave : l.diferencia !== 0 ? c.rojoFila : undefined)}
          columnas={[
            {
              titulo: 'Pallet',
              ancho: 130,
              // Hasta confirmar, el pallet del borrador no existe en el sistema: no hay ficha que abrir.
              celda: (l) =>
                f.pallets.some((p) => p.id === l.pallet) ? (
                  <button type="button" onClick={() => onIr('frac', l.pallet)} style={{ ...enlace, fontSize: 13 }}>
                    {l.pallet}
                  </button>
                ) : (
                  <span style={{ fontFamily: mono, fontSize: 13 }}>{l.pallet}</span>
                ),
            },
            { titulo: 'Artículo', celda: (l) => l.articulo },
            { titulo: 'Declarado', ancho: 90, derecha: true, celda: (l) => numero(l.teorico) },
            { titulo: 'Leído', ancho: 80, derecha: true, celda: (l) => (l.leido ? numero(l.fisico) : '—') },
            {
              titulo: 'Diferencia',
              ancho: 90,
              derecha: true,
              celda: (l) =>
                l.leido && l.diferencia !== 0 ? (
                  <span style={{ color: c.rojo, fontWeight: 600 }}>
                    {l.diferencia > 0 ? '+' : ''}
                    {l.diferencia}
                  </span>
                ) : (
                  '—'
                ),
            },
            { titulo: 'Estado', ancho: 200, celda: (l) => <EstadoChip estado={l.estado} /> },
            {
              titulo: 'Leído por',
              ancho: 200,
              celda: (l) =>
                l.leido ? (
                  <span style={{ fontSize: 12.5, color: c.texto2 }}>
                    {l.leidoEn}
                    {l.leidoPor ? ` · ${l.leidoPor}` : ''}
                  </span>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      </div>
    </div>
  );
}

function Salidas({ f, onIr }: { f: Ficha; onIr: (v: Vista, id?: string) => void }) {
  const puedeVerOrden = useSesion().vistas.includes('frac');
  if (f.salidas.length === 0)
    return (
      <div style={tarjeta}>
        <Vacio icono="call_split" titulo="Sin salidas" detalle="Todavía no salió ningún capacho ni pallet de este contenedor." />
      </div>
    );
  return (
    <div style={{ ...tarjeta, overflow: 'hidden' }}>
      <Tabla
        filas={f.salidas}
        clave={(s) => s.id}
        columnas={[
          { titulo: 'Salida', ancho: 170, celda: (s) => <span style={{ fontFamily: mono, fontSize: 12.5 }}>{s.id}</span> },
          { titulo: 'OT', ancho: 110, celda: (s) =>
              s.orden ? (
                puedeVerOrden ? (
                  <button type="button" onClick={() => onIr('frac', s.orden!)} title="Ver la orden" style={{ ...enlace, fontSize: 12.5 }}>
                    {s.orden}
                  </button>
                ) : (
                  <span style={{ fontFamily: mono, fontSize: 12.5 }}>{s.orden}</span>
                )
              ) : (
                '—'
              ),
          },
          { titulo: 'Artículo', celda: (s) => s.articulo },
          {
            titulo: 'Piezas',
            ancho: 90,
            derecha: true,
            celda: (s) => (s.unidad === 'pallet' ? <span style={{ color: c.texto3 }}>pallet</span> : numero(s.piezas)),
          },
          {
            titulo: 'Pallet',
            ancho: 130,
            celda: (s) => (
              <button type="button" onClick={() => onIr('frac', s.pallet)} style={{ ...enlace, fontSize: 13 }}>
                {s.pallet}
              </button>
            ),
          },
          { titulo: 'Estado', ancho: 120, celda: (s) => <EstadoChip estado={s.estado} /> },
          { titulo: 'Armado', ancho: 100, celda: (s) => s.armadoEn ?? '—' },
          { titulo: 'Entregado', ancho: 100, celda: (s) => s.entregadoEn ?? '—' },
        ]}
      />
    </div>
  );
}
